/**
 * Place a fixed panel against an anchor: below it, flipped above when it would overflow the
 * bottom, and clamped to the viewport on both axes with an edge margin.
 *
 * It measures after the panel has content, which is why the measurement is an effect rather than
 * a render-time calculation.
 */

import { useLayoutEffect, useState, type RefObject } from 'react';

export interface AnchoredPosition {
  top: number;
  left: number;
  flipped: boolean;
}

export interface AnchoredOptions {
  anchorRef: RefObject<HTMLElement | null>;
  panelRef: RefObject<HTMLElement | null>;
  open: boolean;
  gap?: number;
  edge?: number;
}

export function useAnchoredPosition({
  anchorRef,
  panelRef,
  open,
  gap = 8,
  edge = 12,
}: AnchoredOptions): AnchoredPosition {
  const [position, setPosition] = useState<AnchoredPosition>({
    top: 0,
    left: 0,
    flipped: false,
  });

  useLayoutEffect(() => {
    if (!open) return;
    const anchor = anchorRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const a = anchor.getBoundingClientRect();
    const p = panel.getBoundingClientRect();

    let top = a.bottom + gap;
    let flipped = false;
    if (top + p.height > window.innerHeight - edge) {
      top = a.top - gap - p.height;
      flipped = true;
    }
    top = Math.min(Math.max(top, edge), Math.max(edge, window.innerHeight - p.height - edge));

    let left = a.left;
    left = Math.min(Math.max(left, edge), Math.max(edge, window.innerWidth - p.width - edge));

    setPosition({ top, left, flipped });
  }, [open, anchorRef, panelRef, gap, edge]);

  return position;
}
